import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  AlertDialogCloseButton,
  useToast,
  Box,
  Text,
  Flex,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import useAxios from 'axios-hooks';
import { API_ROUTES } from 'utils/constant';
import { ModalType } from 'utils/constant';
import { FileExcelValid } from 'utils/constant';
import SelectController from 'components/Form/SelectController';
import { DomainEmailOption } from 'utils/constant';

const defaultValues = {
  domainEmail: undefined,
};

const ImportUserModal = ({ isOpen, onClose, refetchData }) => {
  const cancelRef = useRef();
  const inputImportRef = useRef();
  const toast = useToast();
  const [error, setError] = useState();
  const [fileSelected, setFileSelected] = useState(null);

  const [{ loading: importLoading }, importUserApi] = useAxios(
    {
      method: 'post',
      url: API_ROUTES.ImportUser,
    },
    { manual: true }
  );

  const schema = yup.object().shape({
    domainEmail: yup.object().nullable().required('Vui lòng chọn domain'),
  });
  const { control, setValue, handleSubmit, reset } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  useEffect(
    () => () => {
      setFileSelected(null);
      reset();
    },
    []
  );

  const onSubmit = values => {
    toast.closeAll();

    if (!fileSelected) {
      setError('Vui lòng chọn file cần import');
      return;
    }

    const formData = new FormData();
    formData.append('domainEmail', values.domainEmail?.value);
    formData.append('userFile', fileSelected);

    importUserApi({ data: formData })
      .then(() => {
        refetchData();
        toast({
          title: 'Import người dùng thành công',
          status: 'success',
          duration: 9000,
          isClosable: true,
        });
        onClose?.(ModalType.Import);
      })
      .catch(error => {
        toast({
          title: error?.response?.data?.errors?.[0]?.msg || error?.response?.data?.msg || 'Import người dùng không thành công',
          status: 'error',
          duration: 9000,
          isClosable: true,
        });
      });
  };

  const handleFileSelect = e => {
    setError('');
    if (e?.target?.files?.[0]) {
      const userFile = e.target.files[0];
      const extensionFile = userFile?.name?.split('.')?.pop();

      if (FileExcelValid.includes(extensionFile)) {
        setFileSelected(userFile);
      } else {
        setFileSelected(null);
        setError('Chỉ hỗ trợ tải lại file định dạng .xlsx, xls');
      }
    }
  };

  return (
    <>
      <AlertDialog
        motionPreset="slideInBottom"
        leastDestructiveRef={cancelRef}
        onClose={() => {
          onClose?.(ModalType.Import);
        }}
        isOpen={isOpen}
        isCentered
      >
        <AlertDialogOverlay />

        <AlertDialogContent>
          <AlertDialogHeader textTransform="uppercase">Import người dùng</AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>
            <form>
              <SelectController control={control} name="domainEmail" label="Domain" isRequired options={DomainEmailOption} />
              <Box alignItems="center">
                <Flex alignItems="center">
                  <input type="file" hidden ref={inputImportRef} onChange={handleFileSelect} />
                  <Button
                    variant="primary"
                    maxH="30px"
                    my="10px"
                    onClick={() => {
                      setError('');
                      inputImportRef?.current?.click();
                    }}
                  >
                    Chọn file
                  </Button>
                  <Text pl="10px">{fileSelected?.name}</Text>
                </Flex>
                {!!error && (
                  <Text color={'red.500'} fontSize="14px">
                    {error}
                  </Text>
                )}
              </Box>
            </form>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button
              ref={cancelRef}
              onClick={() => {
                onClose?.(ModalType.Import);
              }}
            >
              Hủy
            </Button>
            <Button colorScheme="red" ml={3} isLoading={importLoading} onClick={handleSubmit(onSubmit)}>
              Import
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ImportUserModal;
